import { BlockSchema, type BlockType } from './blocks';

/**
 * Admin-side helpers around the block contract mirror (D-page-schema-editor-8a):
 * the page-id vocabulary, the palette labels, a blank-data factory for a freshly
 * added block, the per-block validation used on draft save, and the structural
 * summary the composer renders (NOT the live preview, which is 8c).
 *
 * A composer works on LOOSE draft blocks (`DraftBlock`, data is an open record) so
 * a half-configured block is representable while the author fills it in. On save,
 * each draft block is PARSED against the strict BlockSchema; an invalid block
 * blocks the save (the authoring-time guard). The publish GATE over the whole
 * schema is sub-surface 8b.
 */

/** A block being authored: the strict shape is only enforced on save. */
export interface DraftBlock {
  type: BlockType;
  id: string;
  data: Record<string, unknown>;
}

/** The block palette, in display order, with human labels. */
export const BLOCK_TYPES: readonly BlockType[] = [
  'hero',
  'carousel',
  'storeGrid',
  'categoryStrip',
  'banner',
  'featuredStore',
  'textBlock',
  'divider',
  'pillCta',
];

export const BLOCK_TYPE_LABEL: Record<BlockType, string> = {
  hero: 'Hero',
  carousel: 'Carousel',
  storeGrid: 'Store grid',
  categoryStrip: 'Category strip',
  banner: 'Banner',
  featuredStore: 'Featured store',
  textBlock: 'Text block',
  divider: 'Divider',
  pillCta: 'Pill CTA',
};

/**
 * The page-id vocabulary (cross-repo admin convention): the two fixed pages plus
 * the two templated families. The form validates a pageId against this pattern so
 * an author cannot mint a page the apps do not render.
 */
export const PAGE_ID_PATTERN =
  /^(home|browse-featured|category-[a-z0-9-]+|seasonal-[a-z0-9-]+)$/;

export const PAGE_ID_HELP =
  'home, browse-featured, category-{slug} or seasonal-{name}';

/**
 * Starting data for a freshly added block. Optional fields are left empty; the
 * REQUIRED variant enums (carousel / categoryStrip / banner) start UNSET (empty
 * string) so the author must choose one and an unconfigured variant block cannot
 * be saved; the min-1 reference arrays start empty so they must be filled.
 */
export const blankBlockData = (type: BlockType): Record<string, unknown> => {
  switch (type) {
    case 'hero':
      return { image: '', title: '' };
    case 'carousel':
      return { variant: '', storeIds: [], categoryIds: [] };
    case 'storeGrid':
      return { storeIds: [], columns: 2 };
    case 'categoryStrip':
      return { variant: '', categoryIds: [] };
    case 'banner':
      return { variant: '', image: '', link: { kind: 'store', ref: '' } };
    case 'featuredStore':
      return { storeId: '' };
    case 'textBlock':
      return { body: '' };
    case 'divider':
      return { style: 'thin' };
    case 'pillCta':
      return { label: '', link: { kind: 'page', ref: '' } };
  }
};

/** Parse one draft block against the strict contract. */
export const parseBlock = (block: DraftBlock) => BlockSchema.safeParse(block);

/** Is every block in the draft valid against the contract? */
export const allBlocksValid = (blocks: readonly DraftBlock[]): boolean =>
  blocks.every((b) => parseBlock(b).success);

/** The block types in the draft that fail validation (for the save error). */
export const invalidBlockTypes = (blocks: readonly DraftBlock[]): BlockType[] =>
  blocks.filter((b) => !parseBlock(b).success).map((b) => b.type);

/** Resolve a store slug to its brand name, or the raw slug if unknown. */
export type StoreResolver = (slug: string) => string;
/** Resolve a category slug to its label, or the raw slug if unknown. */
export type CategoryResolver = (slug: string) => string;

const list = (
  refs: unknown,
  resolve: (s: string) => string,
  empty: string,
): string => {
  const arr = Array.isArray(refs) ? (refs as string[]) : [];
  if (arr.length === 0) return empty;
  return arr.map((r) => resolve(r)).join(', ');
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * A short human summary of a block's configured data for the STRUCTURAL view (the
 * arrangeable representation, not the live preview). References are resolved to
 * real store / category names so the author sees what a ref points at.
 */
export const summariseBlock = (
  block: DraftBlock,
  resolveStore: StoreResolver,
  resolveCategory: CategoryResolver,
): string => {
  const d = block.data;
  switch (block.type) {
    case 'hero':
      return str(d.title) || '(no title yet)';
    case 'carousel': {
      const variant = str(d.variant) || '(no variant)';
      const stores = list(d.storeIds, resolveStore, '');
      const cats = list(d.categoryIds, resolveCategory, '');
      const refs = [stores, cats].filter(Boolean).join(' / ');
      return refs ? `${variant}: ${refs}` : variant;
    }
    case 'storeGrid': {
      const stores = list(d.storeIds, resolveStore, 'no stores yet');
      return `${str(d.columns) || (d.columns as number) || 2} columns: ${stores}`;
    }
    case 'categoryStrip': {
      const variant = str(d.variant) || '(no variant)';
      return `${variant}: ${list(d.categoryIds, resolveCategory, 'no categories yet')}`;
    }
    case 'banner': {
      const variant = str(d.variant) || '(no variant)';
      const link = d.link as { kind?: string; ref?: string } | undefined;
      const target = link?.ref ? `${link.kind}:${link.ref}` : 'no link';
      return `${variant} (${target})`;
    }
    case 'featuredStore': {
      const slug = str(d.storeId);
      return slug ? resolveStore(slug) : 'no store yet';
    }
    case 'textBlock':
      return str(d.title) || str(d.body).slice(0, 40) || '(empty)';
    case 'divider':
      return str(d.style) || 'thin';
    case 'pillCta': {
      const link = d.link as { kind?: string; ref?: string } | undefined;
      const target = link?.ref ? `${link.kind}:${link.ref}` : 'no link';
      return `${str(d.label) || '(no label)'} (${target})`;
    }
  }
};
